import { AxiosError, AxiosResponse } from 'axios';
import { action, actionBound, computed, observable, runInAction } from 'mobx';
import { stringify } from 'qs';
import { MandateCustomer } from '../CommonInterfaces';
import config from '../Config';
import { DealerMandatesResponse, DealerRepresentation, Person, RepresentationSelection } from '../DealerInterfaces';
import { UI_WEB_API_PATH } from '../Routes';
import { API, FetchStatus } from '../util/ApiUtil';
import ErrorUtil from '../util/ErrorUtil';
import RootStore from './RootStore';
import RouteStore from './RouteStore';

export class DealerRepresentationStore {
  @observable accessor representation: DealerRepresentation | undefined;
  @observable accessor b2bCustomers: MandateCustomer[] = [];
  @observable accessor b2cCustomers: MandateCustomer[] = [];
  @observable accessor userMandatesNotIncluded: boolean | undefined;
  @observable accessor status: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor selectStatus: FetchStatus = FetchStatus.UNINITIALIZED;

  private routeStore: RouteStore;

  constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
  }

  @actionBound
  public async init() {
    this.status = FetchStatus.FETCHING;
    try {
      await this.loadRepresentation();
    } catch (e: Error | string | any) {
      this.handleLoadError(e);
    }
  }

  @computed
  public get isLoading() {
    return this.status === FetchStatus.FETCHING || this.status === FetchStatus.UNINITIALIZED;
  }

  @computed
  public get isLoaded() {
    return this.status === FetchStatus.FETCHED;
  }

  @computed
  public get isLoadError() {
    return this.status === FetchStatus.ERROR;
  }

  @computed
  public get isRepresentativeChanging() {
    return this.selectStatus === FetchStatus.FETCHING;
  }

  @computed
  public get isRepresentativeSelected(): boolean {
    return this.representation != null && this.representation.person != null;
  }

  @computed
  get dealer() {
    return this.representation && this.representation.dealer;
  }

  @actionBound
  public async loadMandates() {
    if (this.isRepresentativeSelected) {
      await this.findMandates();
    } else {
      this.status = FetchStatus.FETCHED;
    }
  }

  @actionBound
  public async selectCustomer(customer: MandateCustomer) {
    this.selectStatus = FetchStatus.FETCHING;
    await this.updateRepresentation(this.representation?.person || null, customer);
  }

  public clearRepresentation() {
    return this.updateRepresentation(null, null);
  }

  private async updateRepresentation(person: Person | null, customer: MandateCustomer | null) {
    const customerSelection = customer == null ? undefined : { customerId: customer.customerId };
    const personSelection =
      !person?.personalCode || !person.personalCodeCountryCode
        ? undefined
        : {
            personalCode: person.personalCode,
            personalCodeCountryCode: person.personalCodeCountryCode,
          };
    const representationSelection: RepresentationSelection = {
      person: personSelection,
      customer: customerSelection,
    };
    try {
      await API.put('/dealer/representation', representationSelection);
      if (window.top !== window || config.isModule) {
        this.redirectToWebapi();
        return Promise.resolve();
      } else {
        await this.loadRepresentation();
        runInAction(() => {
          this.selectStatus = FetchStatus.FETCHED;
        });
      }
    } catch (e: Error | string | any) {
      runInAction(() => {
        this.selectStatus = FetchStatus.ERROR;
      });
      this.handleLoadError(e);
    }
  }

  private async findMandates() {
    const response = await API.get('/dealer/representation/mandates?');
    this.handleLoadSuccess(response);
  }

  @actionBound
  private handleLoadSuccess(response: AxiosResponse) {
    const mandates: DealerMandatesResponse = response.data;
    this.b2bCustomers = mandates.b2bCustomers || [];
    this.b2cCustomers = mandates.b2cCustomers || [];
    this.userMandatesNotIncluded = mandates.userMandatesNotIncluded;
    this.status = FetchStatus.FETCHED;
  }

  @actionBound
  private handleLoadError(e: AxiosError) {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    this.status = FetchStatus.ERROR;
  }

  private async loadRepresentation() {
    const response = await API.get('/dealer/representation');
    runInAction(() => {
      this.representation = { ...response.data };
    });
    await this.loadMandates();
  }

  private redirectToWebapi() {
    const webapiQueryString = stringify(
      {
        mandateChanged: true,
        allowStatisticsCookies: this.routeStore.getParameter('allowStatisticsCookies'),
      },
      {
        addQueryPrefix: true,
      },
    );
    this.routeStore.route(UI_WEB_API_PATH + webapiQueryString);
  }
}
